import React from "react";
import { format, differenceInHours, differenceInMinutes } from "date-fns";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StaffAttendance, AttendanceType } from "@/models/attendanceModel";
import { useTranslation } from "react-i18next";

interface AttendanceHistoryTableProps {
  records: StaffAttendance[];
  className?: string;
}

export const AttendanceHistoryTable = ({
  records,
  className,
}: AttendanceHistoryTableProps) => {
  const { t } = useTranslation();

  // Get appropriate badge color based on attendance type
  const getTypeBadge = (type: AttendanceType) => {
    switch (type) {
      case "work":
        return (
          <Badge className="bg-green-500 hover:bg-green-600">
            {t("regularWork")}
          </Badge>
        );
      case "overtime":
        return (
          <Badge className="bg-amber-500 hover:bg-amber-600">
            {t("overtime")}
          </Badge>
        );
      case "holiday":
        return (
          <Badge className="bg-blue-500 hover:bg-blue-600">
            {t("holiday")}
          </Badge>
        );
      case "sick":
        return (
          <Badge className="bg-red-500 hover:bg-red-600">
            {t("sickLeave")}
          </Badge>
        );
      case "other":
        return (
          <Badge className="bg-gray-500 hover:bg-gray-600">
            {t("otherAbsence")}
          </Badge>
        );
    }
  };

  // Format time duration
  const formatDuration = (clockIn: Date | null, clockOut: Date | null) => {
    if (!clockIn || !clockOut) {
      return "-";
    }

    const hours = differenceInHours(clockOut, clockIn);
    const remainingMinutes = differenceInMinutes(clockOut, clockIn) % 60;

    return `${hours}h ${remainingMinutes}m`;
  };

  // Format date for display
  const formatDate = (date: Date | string | null) => {
    if (!date) return "-";
    return format(
      typeof date === "string" ? new Date(date) : date,
      "MMM dd, yyyy"
    );
  };

  // Format time for display
  const formatTime = (date: Date | null) => {
    if (!date) return "-";
    return format(date, "hh:mm a");
  };

  const isLeaveRecord =
    records.length > 0 &&
    (records[0].type === "holiday" ||
      records[0].type === "sick" ||
      records[0].type === "other");

  return (
    <Table className={className}>
      <TableCaption>
        {isLeaveRecord ? t("leaveHistory") : t("attendanceHistory")}
      </TableCaption>
      <TableHeader>
        <TableRow>
          {isLeaveRecord ? (
            <>
              <TableHead>{t("leaveType")}</TableHead>
              <TableHead>{t("startDate")}</TableHead>
              <TableHead>{t("endDate")}</TableHead>
              <TableHead>{t("description")}</TableHead>
            </>
          ) : (
            <>
              <TableHead>{t("date")}</TableHead>
              <TableHead>{t("type")}</TableHead>
              <TableHead>{t("clockIn")}</TableHead>
              <TableHead>{t("clockOut")}</TableHead>
              <TableHead>{t("duration")}</TableHead>
            </>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.map((record) => (
          <TableRow key={record.id}>
            {isLeaveRecord ? (
              <>
                <TableCell>{getTypeBadge(record.type)}</TableCell>
                <TableCell>{formatDate(record.leave_from)}</TableCell>
                <TableCell>{formatDate(record.back_on)}</TableCell>
                <TableCell>{record.description || "-"}</TableCell>
              </>
            ) : (
              <>
                <TableCell>{formatDate(record.clock_in)}</TableCell>
                <TableCell>{getTypeBadge(record.type)}</TableCell>
                <TableCell>{formatTime(record.clock_in)}</TableCell>
                <TableCell>{formatTime(record.clock_out)}</TableCell>
                <TableCell>
                  {formatDuration(record.clock_in, record.clock_out)}
                </TableCell>
              </>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
